/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a reason or result code; it renders nothing */
// The offline contracts' side of useAuth (C2 identity, C3 transitions),
// shared by every mounted useAuth(): each instance hears the same Supabase
// auth event, so the decision for one change is made ONCE (single flight per
// change) and every instance applies its answer.
//
//  - A session for a permanent account: the owner is activated (guest ->
//    account), restored (same owner, a rejection cleared, no C3), or, for a
//    DIFFERENT account than the device's owner, C3 runs for the departing
//    owner BEFORE the new session is adopted. Cancelled: the incoming
//    session is removed from this browser and the old owner stays.
//  - A SIGNED_OUT this tab did not cause, with the device still owned by
//    that account and no transition: GoTrue rejected the refresh token
//    (terminal). The owner's data stays visible READ-ONLY ("Sign in again").
//  - No session but an account owns the device (offline past expiry, a
//    refresh pending): the stored profile stands in for the user.
// With the runtime inactive (no IndexedDB, tests) every answer is the live user.
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { readStoredSession, removeLocalSession } from '../lib/localSession'
import { readDeviceIndex } from '../lib/offline/offlineStore'
import { C3_REASON } from '../lib/offline/offline.codes'
import { accountOwner, isAccountOwner, ownerSubject } from '../lib/offline/identityMachine'
import { getIdentity, identitySessionChanged } from '../lib/offline/identity'
import {
  isTransitionInFlight, offlineOwnerProfile, offlineReady, offlineRecoveryBlocked, offlineRuntimeActive, ownerSessionSeen, runAccountTransition,
  sessionLostWithoutSignOut,
} from '../lib/offline/runtime'
import type { StoredProfile } from '../lib/offline/snapshots'

export interface AuthDecision {
  /** The user this tab shows (the live one, or the offline owner's stored profile). */
  user: User | null
  /** `user` is the stored profile of an account without a usable session. */
  offline: boolean
}

const inflight = new Map<string, Promise<AuthDecision>>()
let signOutInFlight = 0

/** useAuth.signOut runs this around its own sign-out (its SIGNED_OUT is not a rejection). */
export async function withOwnSignOut<T>(fn: () => Promise<T>): Promise<T> {
  signOutInFlight += 1
  try {
    return await fn()
  } finally {
    signOutInFlight -= 1
  }
}

function profileOf(u: User): Omit<StoredProfile, 'schema_version'> {
  const md = (u.user_metadata ?? {}) as Record<string, unknown>
  const keep: Record<string, unknown> = {}
  for (const k of ['full_name', 'name', 'avatar_url', 'interface_language', 'saved_decks_migration_dismissed']) if (k in md) keep[k] = md[k]
  return { id: u.id, email: u.email ?? null, user_metadata: keep }
}

/** A User shaped from the stored profile (only what the app reads offline). */
export function userFromProfile(p: StoredProfile): User {
  return {
    id: p.id,
    email: p.email ?? undefined,
    user_metadata: p.user_metadata,
    app_metadata: {},
    aud: 'authenticated',
    created_at: '',
    is_anonymous: false,
  } as unknown as User
}

async function offlineOwnerUser(): Promise<User | null> {
  const id = getIdentity()
  if (!isAccountOwner(id.offlineOwner)) return null
  const p = await offlineOwnerProfile()
  if (p) return userFromProfile(p)
  // No profile stored yet (the first offline load after this shipped): the
  // account still owns the data; show it, nameless, rather than signed out.
  const subject = ownerSubject(id.offlineOwner)
  return subject ? userFromProfile({ schema_version: 1, id: subject, email: null, user_metadata: {} }) : null
}

/** Fail closed: nobody adopted, nothing of any owner shown (findings 15, 16). */
const CLOSED: AuthDecision = { user: null, offline: false }

async function decide(from: string | null, next: User | null, expiresAt: number | null, event: boolean): Promise<AuthDecision> {
  await offlineReady()
  if (!offlineRuntimeActive()) return { user: next, offline: false }
  if (offlineRecoveryBlocked()) return CLOSED
  const permanent = !!next && !(next as { is_anonymous?: boolean }).is_anonymous
  if (next && permanent) {
    const owner = getIdentity().offlineOwner
    const incoming = accountOwner(next.id)
    // Quarantine (finding 15): auth-js already published the incoming
    // session to shared storage. The departing owner's session is unusable
    // from this instant, so nothing of theirs is read or written with it.
    if (isAccountOwner(owner) && owner !== incoming) identitySessionChanged(next.id, expiresAt)
    const r = await ownerSessionSeen(next.id, profileOf(next), expiresAt)
    if (r === 'ok' || r === 'inactive') return { user: next, offline: false }
    if (r === 'blocked') return CLOSED
    // C3 for the departing owner BEFORE the incoming session is adopted.
    const out = await runAccountTransition({ reason: C3_REASON.accountSwitch, incoming })
    if (out.outcome === 'cancelled') {
      // The user kept the old owner: the incoming session leaves this browser.
      await removeLocalSession(supabase)
      identitySessionChanged(null, null)
      return { user: await offlineOwnerUser(), offline: true }
    }
    if (out.outcome !== 'done' && out.outcome !== 'noop') return CLOSED
    // Adopt only a verified result (finding 16): the device now belongs to
    // the incoming account (or nobody), and the stored session is still its.
    const idx = await readDeviceIndex()
    if (idx.pending_wipes.length > 0 || (idx.offline_owner !== incoming && isAccountOwner(idx.offline_owner))) return CLOSED
    const stored = await readStoredSession(supabase)
    if (stored === 'unknown' || !stored || stored.subject?.toLowerCase() !== next.id.toLowerCase()) return CLOSED
    const again = await ownerSessionSeen(next.id, profileOf(next), expiresAt)
    return again === 'ok' ? { user: next, offline: false } : CLOSED
  }
  if (next) return { user: next, offline: false }
  // The session that went away: the event's previous user, else the one C2
  // knew (the first session may have come from getSession, not an event).
  const lost = event ? (from ?? getIdentity().sessionSubject) : null
  identitySessionChanged(null, null)
  if (lost && !isTransitionInFlight() && signOutInFlight === 0) {
    // Never itself a rejection (finding 14): the refresh observer decides.
    const r = await sessionLostWithoutSignOut(lost)
    if (r === 'owner_kept') {
      const u = await offlineOwnerUser()
      return { user: u, offline: !!u }
    }
    return { user: null, offline: false }
  }
  if (isTransitionInFlight() || signOutInFlight > 0) return { user: null, offline: false }
  // No session, but an account owns the device: offline past expiry, a
  // refresh pending, or a rejection from an earlier page.
  const u = await offlineOwnerUser()
  return { user: u, offline: !!u }
}

/** The decision for one auth change, made once for every mounted useAuth(). */
export function decideAuthChange(from: string | null, next: User | null, expiresAt: number | null, opts: { event?: boolean } = {}): Promise<AuthDecision> {
  const event = opts.event === true
  const key = `${event ? 'e' : 'i'}:${from ?? ''}>${next?.id ?? ''}>${expiresAt ?? ''}`
  const existing = inflight.get(key)
  if (existing) return existing
  // Any failure fails CLOSED (finding 16): never the incoming user by default.
  const p = decide(from, next, expiresAt, event).catch((e: unknown): AuthDecision => {
    console.error('Offline auth decision failed; failing closed', e)
    return CLOSED
  })
  inflight.set(key, p)
  void p.finally(() => { if (inflight.get(key) === p) inflight.delete(key) })
  return p
}

export function __resetOfflineAuthForTests(): void {
  inflight.clear()
  signOutInFlight = 0
}
